// Sproutlink Datadog client token for browser-logs
export const DATADOG_CLIENT_TOKEN = 'puba88fe1621ca9a35d248631926e295f33';

type TypeCaptureErrorOptions = {
	tags?: {
		[key: string]: any;
	};
	extra?: {
		[key: string]: any;
	};
	unsetWindowListeners?: boolean;
};

let initialized = false;

const initDatadogLogs = (datadogLogs: typeof import('@datadog/browser-logs').datadogLogs) => {
	if (initialized) {
		return;
	}
	if (!DATADOG_CLIENT_TOKEN) {
		// Token not yet provisioned — skip init. See TODO in utils.ts.
		initialized = true;
		return;
	}
	datadogLogs.init({
		clientToken: DATADOG_CLIENT_TOKEN,
		site: 'datadoghq.com',
		service: 'sproutlink',
		env: process.env.DD_ENV,
		sessionSampleRate: 30,
		// We're already inside captureError when init runs, so we forward this
		// error manually. forwardErrorsToLogs would only catch *future* errors.
		forwardErrorsToLogs: false,
	});
	initialized = true;
};

export const captureError = (
	error: Error,
	{tags, extra, unsetWindowListeners = false}: TypeCaptureErrorOptions = {}
) => {
	if (process.env.NODE_ENV !== 'production') {
		return;
	}

	// eslint-disable-next-line no-console
	console.error(error);

	// Use Promise API rather than async/await so we don't have to include regenerator-runtime
	import('@datadog/browser-logs').then(
		({datadogLogs}) => {
			try {
				initDatadogLogs(datadogLogs);
				datadogLogs.logger.error(error.message, {...tags, ...extra, error});
			} catch (e: any) {
				// eslint-disable-next-line no-console
				console.error('Failed to capture exception', error, e);
			}
		},

		/**
		 * When a dynamic import fails, even if you have a `try/catch` block surrounding it,
		 * it still calls the `window.onunhandledrejection` handler. So, we handle
		 * the rejection case within the Promise directly, rather than in a surrounding
		 * catch block.
		 */
		function failedToImportDatadog(error) {
			// Something else failed, reset window handlers to prevent infinite loop
			// eslint-disable-next-line no-console
			console.error('Logging to Datadog failed', error);
			if (unsetWindowListeners) {
				window.onerror = null;
				window.onunhandledrejection = null;
			}
		}
	);
};

export const attachErrorListener = () => {
	if (process.env.NODE_ENV !== 'production') {
		return;
	}

	window.onerror = (message: any, url: any, line: any, column: any, error?: Error | null) => {
		/**
		 * If no corresponding Error Object is available, `error` will be `null`.
		 * @see https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror#window.onerror
		 */
		if (!error) {
			error = new Error(message);
		}

		return captureError(error, {unsetWindowListeners: true});
	};
	window.onunhandledrejection = (event: any) => captureError(event.reason, {unsetWindowListeners: true});
};
